import React, { useState, useEffect, useMemo } from 'react';
import * as Styles from './UpgradeDetails.styles';

import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { vpsOciActions } from '@/redux/modules/productDetail/vpsOci';
import { useHistory } from 'react-router';
import {
  IconChevron, IconChip, IconMemory, IconDisk, AlertV2, Checkbox, Button, TextField,
} from 'gatorcomponents';
import { useResize } from '@/hooks/useResize';
import useLocale from '@/hooks/useLocale';
import { useParams } from 'react-router-dom';

import upgradeSnappyHalfImage from './media/upgrade-plan-snappy-half.png';
import { formatCurrency } from '@/utils/Formatters/formatCurrency';
import { formatTimestampDate } from '@/utils/Formatters/formatTimestampDate';
import { locale as localeRoutes } from '@/utils/locale';

const UpgradeDetails = ({
  onBackButtonClick,
}) => {
  const dispatch = useDispatch();
  const [checkedTerms, setCheckedTerms] = useState([]);
  const [couponCode, setCouponCode] = useState('');
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [couponAttempted, setCouponAttempted] = useState(false);
  const [appliedCouponCode, setAppliedCouponCode] = useState('');
  const history = useHistory();

  const { id: hostingId } = useParams();

  const windowRect = useResize();
  const { width } = windowRect;
  const isMobile = width < 960;

  const { productDetails: productDetailsLocale } = useLocale();
  const { vpsOci: vpsOciLocale } = productDetailsLocale;
  const upgradeLocale = vpsOciLocale && vpsOciLocale.planDetails && vpsOciLocale.planDetails.upgrade;

  const {
    aboutUpgrade: {
      title: aboutUpgradeTitleLocale = '',
      description: aboutUpgradeDescriptionLocale = '',
      tags: {
        current: aboutUpgradeCurrentTagLocale = '',
        target: aboutUpgradeTargetTagLocale = '',
      } = {},
    } = {},
    financialSummary: {
      title: financialSummaryTitleLocale = '',
      proportionalDifference: financialSummaryProportionalDifferenceLocale = {},
      cycle: cycleLocale = {},
      credits: {
        appliedText: financialSummaryCreditsTextLocale = '',
      } = {},
      coupon: {
        placeholder: financialSummaryCouponPlaceholderLocale = '',
        openInputButton: financialSummaryCouponOpenInputLocale = '',
        discountLabel: financialSummaryCouponDiscountLabelLocale = '',
        addButton: financialSummaryCouponAddButtonLocale = '',
        removeButton: financialSummaryCouponRemoveButtonLocale = '',
        successMessage: financialSummaryCouponSuccessMessageLocale = '',
        errorMessage: financialSummaryCouponErrorMessageLocale = '',
      } = {},
      vatAcronym: financialSummaryVatAcronymLocale = '',
      payment: {
        total: {
          title: financialSummaryTotalTitleLocale = '',
          hint: financialSummaryTotalHintLocale = () => '',
        } = {},
        recurring: {
          title: financialSummaryRecurringTitleLocale = '',
          hint: financialSummaryRecurringHintLocale = () => '',
        },
      } = {},
    } = {},
    informations: informationsLocale = {},
    checkboxConfirmation: checkboxConfirmationLocale = {},
    buttons: upgradeButtonsLocale = {},
  } = (upgradeLocale && upgradeLocale.details) || {};

  const resourcesSuffixLocale = (upgradeLocale && upgradeLocale.resources && upgradeLocale.resources.suffix) || '';

  const {
    upgradeDetailsData,
    upgradeDetailsLoading,
    upgradeSubmitData,
    upgradeInvoiceLoading,
  } = useSelector(state => ({
    upgradeDetailsData: state.vpsOci?.upgrade?.details?.data,
    upgradeDetailsLoading: state.vpsOci?.upgrade?.details?.loading,
    upgradeSubmitData: state.vpsOci?.upgrade?.submit?.data,
    upgradeInvoiceLoading: state.vpsOci?.upgrade?.submit?.loading,
  }), shallowEqual);
  const { invoiceId: upgradeInvoiceId } = upgradeSubmitData || {};

  useEffect(() => {
    const invoiceId = upgradeInvoiceId;
    if (invoiceId) {
      history.push(`${localeRoutes('routes.billing')}${localeRoutes('routes.unpaid')}/${invoiceId}?openInvoice=true`);
    }
  }, [history, hostingId, upgradeInvoiceId]);

  const {
    plan: {
      current: {
        productName: currentProductName,
        memory: currentMemory,
        disk: currentDisk,
        processor: currentProcessor,
        billingCycle: currentBillingCycle,
        price: currentPrice,
      } = {},
      target: {
        productName: targetProductName,
        productId: targetProductId,
        memory: targetMemory,
        disk: targetDisk,
        processor: targetProcessor,
        billingCycle: targetBillingCycle,
        price: targetPrice,
      } = {},
    } = {},
    summary: {
      priceDifference: proportionalDifferencePrice,
      credit: creditPrice,
      vat: vatPrices,
      total: totalPaymentPrice,
      remainingDays,
      coupon: {
        discount: couponDiscount,
        valid: couponValid,
      } = {},
      nextRecurring: {
        amount: nextRecurringPrice,
        billingCycle: nextRecurringBillingCycle,
        dueDate: nextRecurringDueDate,
      } = {},
    } = {},
  } = upgradeDetailsData || {};

  const showCreditPrice = creditPrice > 0;

  const vatListPrice = vatPrices;
  const hasVatPrices = vatListPrice && vatListPrice.length > 0;

  const formatNextRecurringDueDate = useMemo(
    () => (nextRecurringDueDate ? `${formatTimestampDate(nextRecurringDueDate)} (${cycleLocale[nextRecurringBillingCycle]})` : ''),
    [nextRecurringDueDate, cycleLocale, nextRecurringBillingCycle],
  );

  const resetCheckedTerms = () => {
    setCheckedTerms(false);
  };

  useEffect(() => {
    resetCheckedTerms();
  }, [upgradeDetailsData]);

  const handleCheckboxChange = () => {
    setCheckedTerms(true);
  };

  useEffect(() => {
    if (!upgradeDetailsData) {
      dispatch(vpsOciActions.upgrade.details.request({
        hostingId,
        productId: targetProductId,
        billingCycle: targetBillingCycle,
        coupon: null,
      }));
    }
  }, [dispatch, upgradeDetailsData, hostingId, targetProductId, targetBillingCycle]);

  const changeInputCoupon = (e) => {
    const value = e && e.target && e.target.value;
    const upperValue = value.trim() === '' ? '' : value.toUpperCase();
    setCouponCode(upperValue);

    if (appliedCouponCode && upperValue !== '' && upperValue !== appliedCouponCode) {
      setCouponAttempted(false);
    }
  };

  const handleRemoveCoupon = () => {
    dispatch(vpsOciActions.upgrade.details.request({
      hostingId,
      productId: targetProductId,
      billingCycle: targetBillingCycle,
      coupon: null,
    }));
    setCouponCode('');
    setCouponAttempted(false);
    setAppliedCouponCode('');
  };

  const handleSubmitCoupon = () => {
    if (!couponCode || couponCode.trim() === '') return;

    dispatch(vpsOciActions.upgrade.details.request({
      hostingId,
      productId: targetProductId,
      billingCycle: targetBillingCycle,
      coupon: couponCode,
    }));
    setCouponAttempted(true);
    setAppliedCouponCode(couponCode);
  };

  const isCouponValid = couponValid === true;
  const isCouponInvalid = couponAttempted && !upgradeDetailsLoading && couponValid === false;
  const isCouponSuccess = couponAttempted && !upgradeDetailsLoading && couponValid === true;
  const showCouponApplied = isCouponValid && couponDiscount && couponDiscount > 0;
  const couponStatus = isCouponInvalid ? 'error' : 'normal';

  let couponMessage = '';
  if (!upgradeDetailsLoading && couponAttempted) {
    if (isCouponSuccess) {
      couponMessage = financialSummaryCouponSuccessMessageLocale;
    } else if (isCouponInvalid) {
      couponMessage = financialSummaryCouponErrorMessageLocale;
    }
  }

  const isRemoveMode = isCouponValid && appliedCouponCode && (couponCode === '' || couponCode === appliedCouponCode);

  const couponButtonLabel = isRemoveMode ? financialSummaryCouponRemoveButtonLocale : financialSummaryCouponAddButtonLocale;
  const handleCouponButtonClick = isRemoveMode ? handleRemoveCoupon : handleSubmitCoupon;

  const handleOpenCouponInput = () => {
    setShowCouponInput(true);
  };

  const handleBackOffUpgrade = () => {
    onBackButtonClick && onBackButtonClick();
  };

  const handleConfirmUpgrade = () => {
    const body = {
      ...upgradeDetailsData,
      summary: {
        ...upgradeDetailsData.summary,
        acknowledge: checkedTerms,
      },
    };

    dispatch(vpsOciActions.upgrade.submit.request(hostingId, body));
  };

  if (!upgradeDetailsData || Object.keys(upgradeDetailsData).length === 0) return null;

  return (
    <Styles.UpgradeDetails data-testid="vpsoci-upgrade-details">
      <Styles.UpgradeDetailsWrapper data-testid="vpsoci-upgrade-details-confirmation">

        <Styles.UpgradeConfirmationHeader>
          <Styles.UpgradeDetailsTitle data-testid="vpsoci-upgrade-details-confirmation-title">
            {aboutUpgradeTitleLocale}
          </Styles.UpgradeDetailsTitle>
          <Styles.UpgradeDetailsDescription data-testid="vpsoci-upgrade-details-confirmation-description">
            {aboutUpgradeDescriptionLocale}
          </Styles.UpgradeDetailsDescription>
        </Styles.UpgradeConfirmationHeader>

        <Styles.UpgradeConfirmationDetails data-testid="vpsoci-upgrade-details-confirmation-details">
          <Styles.UpgradeCurrentPlanWrapper data-testid="vpsoci-upgrade-details-current-plan-wrapper">
            <Styles.UpgradeCurrentPlanTag data-testid="vpsoci-upgrade-details-current-plan-tag">
              {aboutUpgradeCurrentTagLocale}
            </Styles.UpgradeCurrentPlanTag>
            <Styles.UpgradePlanName data-testid="vpsoci-upgrade-details-current-plan-name">
              {currentProductName}
            </Styles.UpgradePlanName>

            <Styles.UpgradeResourcesItems>
              <Styles.UpgradeResourcesItem>
                <IconChip color="lowPure" />
                <Styles.UpgradeResourcesItemValue data-testid="vpsoci-upgrade-details-current-plan-processor">
                  {currentProcessor}
                  {' '}
                  <Styles.UpgradeResourcesItemValueSuffix>
                    {resourcesSuffixLocale.processor}
                  </Styles.UpgradeResourcesItemValueSuffix>
                </Styles.UpgradeResourcesItemValue>
              </Styles.UpgradeResourcesItem>
              <Styles.UpgradeResourcesItem>
                <IconDisk color="lowPure" />
                <Styles.UpgradeResourcesItemValue data-testid="vpsoci-upgrade-details-current-plan-disk">
                  {currentDisk}
                  {' '}
                  <Styles.UpgradeResourcesItemValueSuffix>
                    {resourcesSuffixLocale.disk}
                  </Styles.UpgradeResourcesItemValueSuffix>
                </Styles.UpgradeResourcesItemValue>
              </Styles.UpgradeResourcesItem>
              <Styles.UpgradeResourcesItem>
                <IconMemory color="lowPure" />
                <Styles.UpgradeResourcesItemValue data-testid="vpsoci-upgrade-details-current-plan-memory">
                  {currentMemory}
                  {' '}
                  <Styles.UpgradeResourcesItemValueSuffix>
                    {resourcesSuffixLocale.memory}
                  </Styles.UpgradeResourcesItemValueSuffix>
                </Styles.UpgradeResourcesItemValue>
              </Styles.UpgradeResourcesItem>
            </Styles.UpgradeResourcesItems>
          </Styles.UpgradeCurrentPlanWrapper>

          <IconChevron
            color="primaryMedium"
            size="md"
            direction={isMobile ? 'bottom' : 'right'}
            testId="vpsoci-upgrade-details-upgrade-chevron"
          />

          <Styles.UpgradeTargetPlanWrapper data-testid="vpsoci-upgrade-details-target-plan-wrapper">
            <Styles.UpgradeTargetPlanTag data-testid="vpsoci-upgrade-details-target-plan-tag">
              {aboutUpgradeTargetTagLocale}
            </Styles.UpgradeTargetPlanTag>

            <Styles.UpgradePlanName data-testid="vpsoci-upgrade-details-target-plan-name">
              {targetProductName}
            </Styles.UpgradePlanName>

            <Styles.UpgradeResourcesItems>
              <Styles.UpgradeResourcesItem>
                <IconChip color="highLight" />
                <Styles.UpgradeResourcesItemValue data-testid="vpsoci-upgrade-details-target-plan-processor">
                  {targetProcessor}
                  {' '}
                  <Styles.UpgradeResourcesItemValueSuffix>
                    {resourcesSuffixLocale.processor}
                  </Styles.UpgradeResourcesItemValueSuffix>
                </Styles.UpgradeResourcesItemValue>
              </Styles.UpgradeResourcesItem>
              <Styles.UpgradeResourcesItem>
                <IconDisk color="highLight" />
                <Styles.UpgradeResourcesItemValue data-testid="vpsoci-upgrade-details-target-plan-disk">
                  {targetDisk}
                  {' '}
                  <Styles.UpgradeResourcesItemValueSuffix>
                    {resourcesSuffixLocale.disk}
                  </Styles.UpgradeResourcesItemValueSuffix>
                </Styles.UpgradeResourcesItemValue>
              </Styles.UpgradeResourcesItem>
              <Styles.UpgradeResourcesItem>
                <IconMemory color="highLight" />
                <Styles.UpgradeResourcesItemValue data-testid="vpsoci-upgrade-details-target-plan-memory">
                  {targetMemory}
                  {' '}
                  <Styles.UpgradeResourcesItemValueSuffix>
                    {resourcesSuffixLocale.memory}
                  </Styles.UpgradeResourcesItemValueSuffix>
                </Styles.UpgradeResourcesItemValue>
              </Styles.UpgradeResourcesItem>
            </Styles.UpgradeResourcesItems>

            <Styles.UpgradePlanSnappyHalf data-testid="vpsoci-upgrade-details-upgrade-plan-snappy-half">
              <Styles.UpgradePlanSnappyHalfImage src={upgradeSnappyHalfImage} />
            </Styles.UpgradePlanSnappyHalf>
          </Styles.UpgradeTargetPlanWrapper>
        </Styles.UpgradeConfirmationDetails>

      </Styles.UpgradeDetailsWrapper>

      <Styles.UpgradeDetailsWrapper data-testid="vpsoci-upgrade-details-financial-summary">
        <Styles.UpgradeDetailsTitle data-testid="vpsoci-upgrade-details-financial-summary-title">
          {financialSummaryTitleLocale}
        </Styles.UpgradeDetailsTitle>

        <Styles.UpgradeFinancialSummaryDetails data-testid="vpsoci-upgrade-details-financial-summary-details">
          <Styles.UpgradeFinancialSummaryDetailsPlanWrapper data-testid="vpsoci-upgrade-details-financial-summary-current-plan">
            <Styles.UpgradeFinancialSummaryDetailsPlanInfo>
              <Styles.UpgradeFinancialSummaryDetailsPlanInfoName data-testid="vpsoci-upgrade-details-financial-summary-current-plan-name">
                {currentProductName}
              </Styles.UpgradeFinancialSummaryDetailsPlanInfoName>
              <Styles.UpgradeFinancialSummaryDetailsPlanInfoCycle data-testid="vpsoci-upgrade-details-financial-summary-current-plan-cycle">
                {aboutUpgradeCurrentTagLocale}
                {' '}
                |
                {' '}
                {cycleLocale.text}
                {' '}
                {cycleLocale[currentBillingCycle]}
              </Styles.UpgradeFinancialSummaryDetailsPlanInfoCycle>
            </Styles.UpgradeFinancialSummaryDetailsPlanInfo>

            <Styles.UpgradeFinancialSummaryDetailsPlanPrice data-testid="vpsoci-upgrade-details-financial-summary-current-plan-price">
              {formatCurrency(currentPrice)}
            </Styles.UpgradeFinancialSummaryDetailsPlanPrice>
          </Styles.UpgradeFinancialSummaryDetailsPlanWrapper>

          <Styles.UpgradeFinancialSummaryDetailsPlanWrapper data-testid="vpsoci-upgrade-details-financial-summary-target-plan">
            <Styles.UpgradeFinancialSummaryDetailsPlanInfo>
              <Styles.UpgradeFinancialSummaryDetailsPlanInfoName data-testid="vpsoci-upgrade-details-financial-summary-target-plan-name">
                {targetProductName}
              </Styles.UpgradeFinancialSummaryDetailsPlanInfoName>
              <Styles.UpgradeFinancialSummaryDetailsPlanInfoCycle data-testid="vpsoci-upgrade-details-financial-summary-target-plan-cycle">
                {aboutUpgradeTargetTagLocale}
                {' '}
                |
                {' '}
                {cycleLocale.text}
                {' '}
                {cycleLocale[targetBillingCycle]}
              </Styles.UpgradeFinancialSummaryDetailsPlanInfoCycle>
            </Styles.UpgradeFinancialSummaryDetailsPlanInfo>

            <Styles.UpgradeFinancialSummaryDetailsPlanPrice data-testid="vpsoci-upgrade-details-financial-summary-target-plan-price">
              {formatCurrency(targetPrice)}
            </Styles.UpgradeFinancialSummaryDetailsPlanPrice>
          </Styles.UpgradeFinancialSummaryDetailsPlanWrapper>

          <Styles.UpgradeFinancialSummaryDetailsPlanWrapper data-testid="vpsoci-upgrade-details-financial-summary-proportional-difference">
            <Styles.UpgradeFinancialSummaryDetailsPlanInfoName data-testid="vpsoci-upgrade-details-financial-summary-proportional-difference-name">
              {financialSummaryProportionalDifferenceLocale}
            </Styles.UpgradeFinancialSummaryDetailsPlanInfoName>

            <Styles.UpgradeFinancialSummaryDetailsPlanPrice data-testid="vpsoci-upgrade-details-financial-summary-proportional-difference-price">
              {formatCurrency(proportionalDifferencePrice)}
            </Styles.UpgradeFinancialSummaryDetailsPlanPrice>
          </Styles.UpgradeFinancialSummaryDetailsPlanWrapper>

          {hasVatPrices && (
            vatListPrice.map((vat) => {
              const slugfyedName = vat && vat.name && vat.name.toLowerCase().replace(/\s+/g, '-');
              return (
                <Styles.UpgradeFinancialSummaryDetailsPlanWrapper data-testid={`vpsoci-upgrade-details-financial-summary-vat-${slugfyedName}`}>
                  <Styles.UpgradeFinancialSummaryDetailsPlanInfoName data-testid={`vpsoci-upgrade-details-financial-summary-vat-${slugfyedName}-name`}>
                    {financialSummaryVatAcronymLocale}
                    {' '}
                    {vat.name}
                    {' '}
                    (
                    {vat.percentage}
                    )
                  </Styles.UpgradeFinancialSummaryDetailsPlanInfoName>

                  <Styles.UpgradeFinancialSummaryDetailsPlanPrice data-testid={`vpsoci-upgrade-details-financial-summary-vat-${slugfyedName}-price`}>
                    {formatCurrency(vat.value)}
                  </Styles.UpgradeFinancialSummaryDetailsPlanPrice>
                </Styles.UpgradeFinancialSummaryDetailsPlanWrapper>
              );
            })
          )}

          <Styles.UpgradeDetailsSeparator />

          <Styles.UpgradeFinancialSummaryDetailsCredits data-testid="vpsoci-upgrade-details-financial-summary-credits">
            <Styles.UpgradeFinancialSummaryDetailsPlanWrapper data-testid="vpsoci-upgrade-details-financial-summary-credits-info">
              <Styles.UpgradeFinancialSummaryDetailsPlanInfoName data-testid="vpsoci-upgrade-details-financial-summary-credits-name">
                {financialSummaryCreditsTextLocale}
              </Styles.UpgradeFinancialSummaryDetailsPlanInfoName>

              {showCreditPrice && (
                <Styles.UpgradeFinancialSummaryDetailsPlanPrice data-testid="vpsoci-upgrade-details-financial-summary-credits-price">
                  -
                  {' '}
                  {formatCurrency(creditPrice)}
                </Styles.UpgradeFinancialSummaryDetailsPlanPrice>
              )}
            </Styles.UpgradeFinancialSummaryDetailsPlanWrapper>

            <Styles.UpgradeFinancialSummaryDetailsCouponWrapper data-testid="vpsoci-upgrade-details-financial-summary-coupon-wrapper">
              {showCouponInput ? (
                <>
                  <Styles.UpgradeFinancialSummaryDetailsCouponInputWrapper data-testid="vpsoci-upgrade-details-financial-summary-coupon-input-wrapper">
                    <TextField
                      testId="vpsoci-upgrade-details-financial-summary-coupon-input"
                      leadingIcon="IconDiscount"
                      label={financialSummaryCouponPlaceholderLocale}
                      value={couponCode}
                      onChange={changeInputCoupon}
                      status={couponStatus}
                      helpText={couponMessage}
                    />
                    <Button
                      variant="secondary"
                      size="xlarge"
                      label={couponButtonLabel}
                      onClick={handleCouponButtonClick}
                      loading={upgradeDetailsLoading}
                      testId="vpsoci-upgrade-details-financial-summary-coupon-add-button"
                    />
                  </Styles.UpgradeFinancialSummaryDetailsCouponInputWrapper>

                  {showCouponApplied && (
                    <Styles.UpgradeFinancialSummaryDetailsPlanWrapper data-testid="vpsoci-upgrade-details-financial-summary-coupon-applied">
                      <Styles.UpgradeFinancialSummaryDetailsPlanInfoName data-testid="vpsoci-upgrade-details-financial-summary-coupon-applied-name">
                        {financialSummaryCouponDiscountLabelLocale}
                      </Styles.UpgradeFinancialSummaryDetailsPlanInfoName>

                      <Styles.UpgradeFinancialSummaryDetailsPlanPrice data-testid="vpsoci-upgrade-details-financial-summary-coupon-applied-price">
                        -
                        {' '}
                        {formatCurrency(couponDiscount)}
                      </Styles.UpgradeFinancialSummaryDetailsPlanPrice>
                    </Styles.UpgradeFinancialSummaryDetailsPlanWrapper>
                  )}
                </>
              ) : (
                <Styles.UpgradeFinancialSummaryDetailsCouponLink
                  onClick={(e) => {
                    e.preventDefault();
                    handleOpenCouponInput();
                  }}
                  data-testid="vpsoci-upgrade-details-financial-summary-coupon-link"
                >
                  {financialSummaryCouponOpenInputLocale}
                </Styles.UpgradeFinancialSummaryDetailsCouponLink>
              )}
            </Styles.UpgradeFinancialSummaryDetailsCouponWrapper>
          </Styles.UpgradeFinancialSummaryDetailsCredits>

          <Styles.UpgradeDetailsSeparator />

          <Styles.UpgradeFinancialSummaryDetailsPlanWrapper data-testid="vpsoci-upgrade-details-financial-summary-total">
            <Styles.UpgradeFinancialSummaryDetailsPlanInfo>
              <Styles.UpgradeFinancialSummaryTotalName data-testid="vpsoci-upgrade-details-financial-summary-total-name">
                {financialSummaryTotalTitleLocale}
              </Styles.UpgradeFinancialSummaryTotalName>
              <Styles.UpgradeFinancialSummaryDetailsPlanInfoCycle data-testid="vpsoci-upgrade-details-financial-summary-total-cycle">
                {financialSummaryTotalHintLocale(remainingDays)}
              </Styles.UpgradeFinancialSummaryDetailsPlanInfoCycle>
            </Styles.UpgradeFinancialSummaryDetailsPlanInfo>

            <Styles.UpgradeFinancialSummaryTotalPrice data-testid="vpsoci-upgrade-details-financial-summary-total-price">
              {formatCurrency(totalPaymentPrice)}
            </Styles.UpgradeFinancialSummaryTotalPrice>
          </Styles.UpgradeFinancialSummaryDetailsPlanWrapper>

          <Styles.UpgradeDetailsSeparator />

          <Styles.UpgradeFinancialSummaryDetailsNextRecurringWrapper data-testid="vpsoci-upgrade-details-financial-summary-next-recurring">
            <Styles.UpgradeFinancialSummaryDetailsPlanInfo>
              <Styles.UpgradeFinancialSummaryDetailsPlanInfoName data-testid="vpsoci-upgrade-details-financial-summary-next-recurring-name">
                {financialSummaryRecurringTitleLocale}
              </Styles.UpgradeFinancialSummaryDetailsPlanInfoName>
              <Styles.UpgradeFinancialSummaryDetailsPlanInfoCycle data-testid="vpsoci-upgrade-details-financial-summary-next-recurring-due-date">
                {financialSummaryRecurringHintLocale(formatNextRecurringDueDate)}
              </Styles.UpgradeFinancialSummaryDetailsPlanInfoCycle>
            </Styles.UpgradeFinancialSummaryDetailsPlanInfo>

            <Styles.UpgradeFinancialSummaryDetailsPlanPrice data-testid="vpsoci-upgrade-details-financial-summary-next-recurring-price">
              {formatCurrency(nextRecurringPrice)}
            </Styles.UpgradeFinancialSummaryDetailsPlanPrice>
          </Styles.UpgradeFinancialSummaryDetailsNextRecurringWrapper>


        </Styles.UpgradeFinancialSummaryDetails>

        <Styles.UpgradeDetailsSeparator />

        <Styles.UpgradeDetailsAlert data-testid="vpsoci-upgrade-details-alert-wrapper">
          <AlertV2
            title={informationsLocale.title}
            description={informationsLocale.description}
            testId="vpsoci-upgrade-details-alert"
            variant="info"
            width="full"
            withoutBackground={false}
          />
        </Styles.UpgradeDetailsAlert>

        <Styles.UpgradeDetailsTerms data-testid="vpsoci-upgrade-details-terms">
          <Styles.UpgradeDetailsTermsCheckboxLabel
            key="confirmUpgradeTerm"
            htmlFor="checkAgreedTerms-confirmUpgradeTerm"
            data-testid="agreedTerms-confirmUpgradeTerm"
            onClick={(e) => {
              e.preventDefault();
              handleCheckboxChange();
            }}
          >
            <Checkbox
              checked={checkedTerms}
              id="checkAgreedTerms-confirmUpgradeTerm"
              onChange={() => handleCheckboxChange()}
            />
            <Styles.UpgradeDetailsTermsText>
              {checkboxConfirmationLocale}
            </Styles.UpgradeDetailsTermsText>
          </Styles.UpgradeDetailsTermsCheckboxLabel>
        </Styles.UpgradeDetailsTerms>

        <Styles.UpgradeDetailsActions data-testid="vpsoci-upgrade-details-actions">
          <Button
            variant="secondary"
            size="large"
            width={isMobile ? 'full' : 'fit'}
            onClick={handleBackOffUpgrade}
            testId="vpsoci-upgrade-details-cancel-upgrade-button"
            label={upgradeButtonsLocale.cancel}
          />
          <Button
            variant="primary"
            size="large"
            width={isMobile ? 'full' : 'fit'}
            disabled={!checkedTerms || upgradeDetailsLoading}
            loading={upgradeInvoiceLoading}
            onClick={handleConfirmUpgrade}
            testId="vpsoci-upgrade-details-confirm-upgrade-button"
            label={upgradeButtonsLocale.confirm}
          />
        </Styles.UpgradeDetailsActions>
      </Styles.UpgradeDetailsWrapper>
    </Styles.UpgradeDetails>
  );
};

export default UpgradeDetails;
